import { Customer } from '@features/customers/models/customer.model';
import { LocationModel } from '@features/customers/models/location.model';
import { Driver } from '@features/fleets/models/driver.model';
import { Fleet } from '@features/fleets/models/fleet.model';
import { Trailer } from '@features/fleets/models/trailer.model';
import { TruckType } from '@features/fleets/models/truck-type.model';
import { Truck } from '@features/fleets/models/truck.model';
import { ShipmentDeliveryNote } from '@features/invoices/models/shipment-delivery-note.model';
import { ShipmentInvoice } from '@features/invoices/models/shipment-invoice.model';
import { ContactPerson } from '@features/receivers/models/contact-person.model';
import { Receiver } from '@features/receivers/models/receiver.model';
import { User } from '@features/users/models';
import { ShipmentStatus, ShipmentCreationTemplateTypes, WccIntegrationStatusType, FleetInvoiceStatus, InvoiceStatus, ShipmentDirection } from '@shared-features/enums';
import { UserInvoiceType } from '@shared-features/enums/user-invoice-type.enum';
import { Media } from '@shared-features/models';
import { CustomerType } from '@shared/enums';
import { DynamicField } from '@shared/models/dynamic-field.model';
import { MeasurementUnit } from '@shared/models/measurement-unit.model';
import { Model } from '@shared/models/model';
import { Expose, Transform, Type } from 'class-transformer';
import { Transaction } from '@features/invoices/models/transaction.model';
import { ProductType } from '@features/shipments/models/product-type.model';
import { ShipmentHistoryItem } from '@features/shipments/models/shipment-history-item.model';
import { ShipmentRequest } from '@features/shipments/models/shipment-request.model';
import { ShipmentLabel } from '@features/shipments/models/shipment-label.model';
import { RequestLog } from '@features/shipments/models/request-log.model';
import { GateDefinition } from '@features/shipments/models/gate-definition.model';
import { ShipmentMetadata } from '@features/shipments/models/shipment-metadata.model';
import { BayanWayBill } from '@features/shipments/models/bayan-way-bill.model';
import { ShipmentCreationType } from '@features/shipments/enums/shipment-creation-type.enum';
import { VatType } from '@features/shipments/enums/vat-type.enum';
import { BidderType } from '@features/shipment-creation-template/enums/bidder-type.enum';
import { BidStatus } from '@features/shipments/enums/bid-status.enum';


export class Shipment extends Model {
  @Expose()
  id: number = null;

  @Expose()
  uuid?: string;

  @Expose()
  code?: string;

  @Expose()
  name?: string;

  @Expose()
  status?: ShipmentStatus;

  @Expose()
  direction?: ShipmentDirection;

  @Expose()
  bidStatus?: BidStatus;

  @Expose()
  weight?: number;

  @Expose()
  fleetSpotRate?: number;

  @Expose()
  customerSpotRate?: number;

  @Expose()
  isFreeLanced?: boolean;

  @Expose()
  notes?: string;

  @Type(() => Customer)
  @Expose()
  customer?: Customer = new Customer();

  @Type(() => Customer)
  @Expose()
  childCustomer?: Customer = new Customer();

  @Type(() => ProductType)
  @Expose()
  productType?: ProductType = new ProductType();

  @Type(() => TruckType)
  @Expose()
  truckType?: TruckType = new TruckType();

  @Type(() => TruckType)
  @Expose()
  optionalTruckTypes?: TruckType[];

  @Type(() => Receiver)
  @Expose()
  receiver?: Receiver = new Receiver();

  @Type(() => Driver)
  @Expose()
  driver?: Driver = new Driver();

  @Type(() => Truck)
  @Expose()
  truck?: Truck = new Truck();

  @Type(() => Fleet)
  @Expose()
  fleet?: Fleet = new Fleet();

  @Type(() => LocationModel)
  @Expose()
  pickUpLocation?: LocationModel = new LocationModel();

  @Type(() => LocationModel)
  @Expose()
  dropOffLocation?: LocationModel = new LocationModel();

  @Expose()
  @Type(() => ShipmentHistoryItem)
  history: ShipmentHistoryItem[] = [];

  @Type(() => Transaction)
  @Expose()
  transactions?: Transaction[] = [];

  @Expose()
  repeatedNumber?: number = null;

  @Type(() => LocationModel)
  @Expose()
  receiverLocation?: LocationModel = new LocationModel();

  @Type(() => ContactPerson)
  @Expose()
  contactPerson?: ContactPerson = new ContactPerson();

  @Expose()
  @Transform(({ value }) => (value ? new Date(value) : null), { toClassOnly: true })
  pickedDateTime?: Date;

  @Expose()
  @Transform(({ value }) => (value ? new Date(value) : null), { toClassOnly: true })
  pickUpDate?: Date;

  @Expose()
  @Transform(({ value }) => (value ? new Date(value) : null), { toClassOnly: true })
  deliveryDate: Date;

  @Expose()
  @Transform(({ value }) => (value ? new Date(value) : null), { toClassOnly: true })
  createdAt?: Date;

  @Expose()
  @Transform(({ value }) => (value ? new Date(value) : null), { toClassOnly: true })
  updatedAt?: Date;

  @Expose()
  totalFleetCost: number;

  @Expose()
  creatorType?: string;

  @Type(() => User)
  @Expose()
  createdBy?: User = new User();

  @Type(() => User)
  @Expose()
  updatedBy?: User = new User();

  @Expose()
  priceComment?: string;

  @Expose()
  totalProductPrice?: number;

  @Expose()
  costComment?: string;

  @Type(() => ShipmentDeliveryNote)
  @Expose()
  deliveryNote?: ShipmentDeliveryNote[] = [];

  @Expose()
  customerPrice?: number;

  @Expose()
  fleetPrice?: number;

  @Expose()
  customerReturnCharge?: number;

  @Expose()
  fleetReturnCharge?: number;

  @Expose()
  customerWaitingCharge?: number;

  @Expose()
  fleetWaitingCharge?: number;

  @Expose()
  customerClearanceFees?: number;

  @Expose()
  weightingFine?: number;

  @Expose()
  damageCost?: number;

  @Expose()
  archivedTrackingUrl?: string;

  @Expose()
  archivedAvlTracking?: string;

  @Type(() => Driver)
  @Expose()
  coDriver?: Driver = new Driver();

  @Expose()
  otp?: string;

  @Expose()
  referenceNumber?: number;

  @Expose()
  etaTime?: number;

  @Expose()
  detentionCharge: number;

  @Expose()
  multiDestinationChargePrice?: number;

  @Expose()
  multiDestinationChargeCost?: number;

  @Expose()
  fleetClearanceFees?: number;

  @Expose()
  fleetWeightingFine?: number;

  @Expose()
  advancedPayment?: number;

  @Expose()
  deliveryNoteComment?: string;

  @Expose()
  customerWaitingChargePerDay?: string;

  @Expose()
  deliveryNoteNumber?: string;

  @Expose()
  multiDestinationCharge?: number;

  @Expose()
  returnCharge?: number;

  @Expose()
  waitingDays?: number;

  @Expose()
  shipmentDelayed?: boolean;

  @Expose()
  weightMeasurementUnit?: MeasurementUnit;

  @Expose()
  quantity?: number;

  @Type(() => ShipmentRequest)
  @Expose()
  requests?: ShipmentRequest[];

  @Expose() driverAllowance?: number;

  selected?: boolean = false;

  @Type(() => Trailer)
  @Expose()
  trailer?: Trailer;

  @Expose() droppingOffTime?: number;
  @Expose() loadingOffTime?: number;

  @Expose()
  isMobileTrackingAvailable?: boolean;

  @Expose()
  isAvlTrackingAvailable?: boolean;

  @Type(() => ShipmentLabel)
  @Expose()
  label?: ShipmentLabel[] = [];

  @Expose() delayedAction?: boolean;

  @Expose()
  isVerifiedByBayan?: boolean;

  @Expose()
  isBayanReady?: boolean;

  @Type(() => RequestLog)
  @Expose()
  requestLogHistory?: RequestLog[] = [];

  @Expose()
  archivedAvlTrackingUrl?: string;

  @Type(() => Customer)
  @Expose()
  dropOffCustomer?: Customer = new Customer();

  @Type(() => User)
  @Expose()
  dropOffUser?: User = new User();

  @Type(() => User)
  @Expose()
  pickUpUser: User = new User();

  @Expose()
  shipmentCreationTemplate?: ShipmentCreationTemplateTypes;

  @Expose()
  loadedAmount?: number;

  @Expose()
  fleetReferenceNumber?: number;

  @Expose()
  @Transform(({ value }) => (value ? new Date(value) : null), { toClassOnly: true })
  lastJobUpdate?: Date;

  @Expose()
  wccId?: number;

  @Expose()
  wccIntegrationStatus: WccIntegrationStatusType;

  @Expose()
  noOfRequests: number;

  @Expose()
  fleetInvoiceStatus?: FleetInvoiceStatus;

  @Expose()
  customerInvoiceStatus?: InvoiceStatus;

  @Expose()
  gate?: GateDefinition;

  @Expose()
  numOfPallets?: number;

  @Expose()
  noOfPallets?: number;

  @Expose()
  noOfCustomers?: number;

  @Expose()
  noOfOrders: number;

  @Expose()
  etaInSeconds: Date;

  @Expose()
  waitingChargeProfAttachment: Media;

  @Expose()
  waitingChargeProfId: number;

  @Type(() => ShipmentInvoice)
  @Expose()
  invoice: ShipmentInvoice;

  @Expose()
  @Type(() => ShipmentMetadata)
  shipmentMetadata?: ShipmentMetadata;

  @Expose()
  violated: boolean;

  @Expose()
  isPlanned: boolean;

  @Expose()
  deliveryNoteReference: string;

  @Expose()
  driverAssignCount: number;

  @Expose() startShipment: boolean;
  /** Shipment was created onto the marketplace instead of being assigned to a specific fleet. */
  @Expose() isBroadcasted: boolean;
  @Expose() forceClose: boolean;
  @Expose() assignAndAutoStart: boolean;
  @Expose() comment: string;

  @Expose() customerName: string;

  @Type(() => BayanWayBill)
  @Expose()
  bayanWaybills?: BayanWayBill[] = [];

  @Type(() => DynamicField)
  @Expose()
  dynamicFields?: DynamicField[] = [];

  @Expose() hasDynamicFields?: boolean;

  @Expose() serviceType?: UserInvoiceType;

  @Expose()
  shipmentCreationType: ShipmentCreationType;

  @Expose()
  vatType: VatType;

  @Expose()
  volume: number;

  @Expose()
  purchaseOrder?: string;

  @Expose()
  date?: Date;

  @Expose()
  bayanTripId: number;

  @Expose()
  bayanWaybillId: number;

  @Expose()
  pickupGateName: string;

  @Expose()
  shortDynamicLink: string;

  @Expose()
  isPaidForDriver: boolean;

  @Expose()
  customerType: CustomerType;

  @Expose()
  totalPrice: number;

  @Expose()
  productPrice: number;

  @Expose()
  customerPreferences: BidderType[];

  @Expose()
  isAutomated: boolean;

  constructor() {
    super();
  }

  public get shipmentName() {
    if (this.name) {
      return '#' + this.name;
    } else {
      return '#' + this.code;
    }
  }
}
